import type { RequestOptions } from '@@/plugin-request/request';
import type { RequestConfig } from '@umijs/max';
import { buildApiPath } from '@/constants';
import { openAppNotification } from '@/utils/antdApp';
import { clearToken, forceLogout } from '@/utils/token';

// 创建一个全局的消息实例，避免使用静态函数
let globalMessageApi: any = null;

// 设置全局消息API实例的函数
export const setGlobalMessageApi = (messageApi: any) => {
  globalMessageApi = messageApi;
};

// 安全的消息显示函数
const showMessage = (type: 'success' | 'error' | 'warning' | 'info', content: string) => {
  if (globalMessageApi) {
    globalMessageApi[type](content);
  } else {
    // 如果全局实例不可用，使用 console 输出（开发环境）
    console[type === 'error' ? 'error' : 'log'](`[${type.toUpperCase()}]: ${content}`);
  }
};

// 错误处理方案： 错误类型
enum ErrorShowType {
  SILENT = 0,
  WARN_MESSAGE = 1,
  ERROR_MESSAGE = 2,
  NOTIFICATION = 3,
  REDIRECT = 9,
}
// 与后端约定的响应数据格式
interface ResponseStructure {
  success: boolean;
  data: any;
  errorCode?: number;
  errorMessage?: string;
  message?: string; // 支持后端返回的message字段
  showType?: ErrorShowType;
}

/**
 * @name 错误处理
 * pro 自带的错误处理， 可以在这里做自己的改动
 * @doc https://umijs.org/docs/max/request#配置
 */
export const errorConfig: RequestConfig = {
  // 错误处理： umi@3 的错误处理方案。
  errorConfig: {
    // 错误抛出
    errorThrower: (res: any) => {
      const { success, data, errorCode, errorMessage, message, showType } =
        res as unknown as ResponseStructure;
      if (!success) {
        // 优先使用message字段，其次使用errorMessage字段
        const errorMsg = message || errorMessage || '未知错误';
        const error: any = new Error(errorMsg);
        error.name = 'BizError';
        error.info = { errorCode, errorMessage: errorMsg, showType, data };
        throw error; // 抛出自制的错误
      }
    },
    // 错误接收及处理
    errorHandler: (error: any, opts: any) => {
      if (opts?.skipErrorHandler) throw error;
      // 我们的 errorThrower 抛出的错误。
      if (error.name === 'BizError') {
        const errorInfo: ResponseStructure | undefined = error.info;
        if (errorInfo) {
          const { errorMessage, errorCode } = errorInfo;
          switch (errorInfo.showType) {
            case ErrorShowType.SILENT:
              // do nothing
              break;
            case ErrorShowType.WARN_MESSAGE:
              showMessage('warning', errorMessage || '未知警告');
              break;
            case ErrorShowType.ERROR_MESSAGE:
              showMessage('error', errorMessage || '未知错误');
              break;
            case ErrorShowType.NOTIFICATION:
              openAppNotification({
                description: errorMessage,
                message: errorCode,
              });
              break;
            case ErrorShowType.REDIRECT:
              // TODO: redirect
              break;
            default:
              showMessage('error', errorMessage || '未知错误');
          }
        }
      } else if (error.response) {
        // Axios 的错误
        // 请求成功发出且服务器也响应了状态码，但状态代码超出了 2xx 的范围

        // 检查是否跳过错误处理
        if (opts?.skipErrorHandler) {
          throw error;
        }

        const status = error.response.status;
        const responseData = error.response.data;

        // 处理 401 状态码，自动跳转登录页面
        if (status === 401) {
          clearToken();
          window.location.href = '/user/login';
          throw new Error('用户未授权，请重新登录');
        }

        // 优先使用服务器返回的自定义错误消息
        let errorMessage = '';
        if (responseData?.message) {
          errorMessage = responseData.message;
        } else if (responseData?.errorMessage) {
          errorMessage = responseData.errorMessage;
        } else {
          // 使用通用错误消息
          switch (status) {
            case 400:
              errorMessage = '请求参数错误';
              break;
            case 401:
              errorMessage = '未授权访问，请重新登录';
              break;
            case 403:
              errorMessage = '权限不足，无法执行此操作';
              break;
            case 404:
              errorMessage = '请求的资源不存在';
              break;
            case 405:
              errorMessage = '请求方法不允许';
              break;
            case 408:
              errorMessage = '请求超时';
              break;
            case 409:
              errorMessage = '资源冲突';
              break;
            case 422:
              errorMessage = '请求格式正确但语义错误';
              break;
            case 429:
              errorMessage = '请求过于频繁，请稍后重试';
              break;
            case 500:
              errorMessage = '系统内部错误，请联系管理员';
              break;
            case 502:
              errorMessage = '系统异常，请稍后再试～';
              break;
            case 503:
              errorMessage = '服务不可用';
              break;
            case 504:
              errorMessage = '网关超时';
              break;
            default:
              errorMessage = `请求失败 (${status})`;
          }
        }

        showMessage('error', errorMessage);
      } else if (error.request) {
        // 请求已经成功发起，但没有收到响应
        // \`error.request\` 在浏览器中是 XMLHttpRequest 的实例，
        // 而在node.js中是 http.ClientRequest 的实例
        if (!opts?.skipErrorHandler) {
          showMessage('error', 'None response! Please retry.');
        }
      } else {
        // 发送请求时出了点问题
        if (!opts?.skipErrorHandler) {
          showMessage('error', 'Request error, please retry.');
        }
      }
    },
  },

  // 请求拦截器
  requestInterceptors: [
    (config: RequestOptions) => {
      // 统一处理 API 路径，自动添加环境前缀
      if (config?.url && !config.url.startsWith('http')) {
        const apiPath = buildApiPath(config.url);
        config.url = apiPath;
      }

      const publicApis = ['/auth', '/login/account', '/currentUser', '/captcha', '/own/logout', '/own']; // 免登录鉴权接口列表
      const isPublicApi = publicApis.some(api => config.url?.includes(api));

      if (!isPublicApi) {
        const token = localStorage.getItem('jianyin_token');
        if (token) {
          config.headers = {
            ...config.headers,
            // 'Authorization': `Bearer ${token}`
          };
        }
      }

      return config;
    },
  ],

  // 响应拦截器
  responseInterceptors: [
    (response: any) => {
      // 拦截响应数据，进行个性化处理
      const { data } = response as unknown as ResponseStructure;

      // 移除这里的错误提示，让错误处理器统一处理
      // if (data?.success === false) {
      //   showMessage('error', '请求失败！');
      // }

      // 处理 401 状态码，自动跳转登录页面
      // 优先检查 httpStatusCode，然后检查 statusCode，最后检查 response.status
      const statusCode = Number(data?.httpStatusCode || data?.statusCode || response?.status);
      if (statusCode === 401) {
        // 兼容 HTTP 200 包装的未登录响应；强制解绑后的下一次请求立即进入登录页。
        clearToken();
        if (!['/user/login', '/user/login-classic'].includes(window.location.pathname)) {
          forceLogout();
        }
        throw new Error('用户未授权，请重新登录');
      }

      return response;
    },
  ],
};
